import { validateEventGeometry, type EventGeometry } from "@shared/eventGeography";
import type { NormalizedSignal } from "./types";

export const WHO_COUNTRY_BOUNDARY_URL = "/maps/natural-earth-admin0-countries-110m-v5.1.2.geojson";
export const WHO_COUNTRY_BOUNDARY_MAX_BYTES = 300 * 1024;
const MAX_FEATURES = 200;
const MAX_POSITIONS = 15_000;
const LOAD_TIMEOUT_MS = 5_000;
const MAX_COUNTRY_POLYGONS = 32;
const MAX_RINGS_PER_POLYGON = 16;
const MAX_POSITIONS_PER_RING = 600;
const MAX_POSITIONS_PER_GEOMETRY = 4_096;

export type CountryBoundaryIndex = Readonly<Record<string, EventGeometry>>;

function frozenIndex(entries: Array<[string, EventGeometry]> = []): CountryBoundaryIndex {
  return Object.freeze(Object.assign(Object.create(null), Object.fromEntries(entries)));
}

export const EMPTY_COUNTRY_BOUNDARIES = frozenIndex();

function exactKeys(value: object, expected: string[]) {
  const keys = Object.keys(value).sort();
  return keys.length === expected.length && keys.every((key, index) => key === expected[index]);
}

function countPositions(value: unknown): number {
  if (!Array.isArray(value)) return 0;
  if (value.length === 2 && value.every(Number.isFinite)) return 1;
  return value.reduce((total, item) => total + countPositions(item), 0);
}

function freezeCoordinates(value: unknown): void {
  if (!Array.isArray(value)) return;
  for (const item of value) freezeCoordinates(item);
  Object.freeze(value);
}

function freezeGeometry(geometry: EventGeometry): EventGeometry {
  freezeCoordinates(geometry.coordinates);
  return Object.freeze(geometry);
}

function isPosition(value: unknown): value is [number, number] {
  return Array.isArray(value) && value.length === 2 && value.every(Number.isFinite)
    && value[0] >= -180 && value[0] <= 180 && value[1] >= -90 && value[1] <= 90;
}

function validCountryRing(value: unknown) {
  if (!Array.isArray(value) || value.length < 4 || value.length > MAX_POSITIONS_PER_RING || !value.every(isPosition)) return false;
  const first = value[0];
  const last = value[value.length - 1];
  if (first[0] !== last[0] || first[1] !== last[1]) return false;
  let twiceArea = 0;
  for (let index = 0; index < value.length - 1; index += 1) {
    twiceArea += value[index][0] * value[index + 1][1] - value[index + 1][0] * value[index][1];
  }
  return Number.isFinite(twiceArea) && Math.abs(twiceArea) > 1e-12;
}

function validCountryPolygon(value: unknown) {
  return Array.isArray(value) && value.length > 0 && value.length <= MAX_RINGS_PER_POLYGON && value.every(validCountryRing);
}

function validateCountryBoundaryGeometry(value: unknown): EventGeometry | undefined {
  const strict = validateEventGeometry(value);
  if (strict && strict.type !== "Point") return strict;
  if (!value || typeof value !== "object") return undefined;
  const candidate = value as { type?: unknown; coordinates?: unknown };
  if (candidate.type === "Polygon" && validCountryPolygon(candidate.coordinates)
    && countPositions(candidate.coordinates) <= MAX_POSITIONS_PER_GEOMETRY) {
    return candidate as EventGeometry;
  }
  if (candidate.type === "MultiPolygon" && Array.isArray(candidate.coordinates)
    && candidate.coordinates.length > 0 && candidate.coordinates.length <= MAX_COUNTRY_POLYGONS
    && candidate.coordinates.every(validCountryPolygon)
    && countPositions(candidate.coordinates) <= MAX_POSITIONS_PER_GEOMETRY) {
    return candidate as EventGeometry;
  }
  return undefined;
}

export function parseCountryBoundaryCollection(value: unknown): CountryBoundaryIndex {
  if (!value || typeof value !== "object" || !exactKeys(value, ["features", "type"])) {
    throw new Error("Country boundaries must be a FeatureCollection");
  }
  const collection = value as { type?: unknown; features?: unknown };
  if (collection.type !== "FeatureCollection" || !Array.isArray(collection.features)) {
    throw new Error("Country boundaries must be a FeatureCollection");
  }
  if (collection.features.length > MAX_FEATURES) throw new Error("Country boundary feature limit exceeded");

  const entries: Array<[string, EventGeometry]> = [];
  const codes = new Set<string>();
  let totalPositions = 0;
  for (const rawFeature of collection.features) {
    if (!rawFeature || typeof rawFeature !== "object" || !exactKeys(rawFeature, ["geometry", "properties", "type"])) {
      throw new Error("Country boundary feature schema is invalid");
    }
    const feature = rawFeature as { type?: unknown; properties?: unknown; geometry?: unknown };
    if (feature.type !== "Feature" || !feature.properties || typeof feature.properties !== "object"
      || !exactKeys(feature.properties, ["iso2", "name"])) {
      throw new Error("Country boundary feature schema is invalid");
    }
    if (!feature.geometry || typeof feature.geometry !== "object" || !exactKeys(feature.geometry, ["coordinates", "type"])) {
      throw new Error("Country boundary geometry schema is invalid");
    }
    const properties = feature.properties as { iso2?: unknown; name?: unknown };
    if (typeof properties.iso2 !== "string" || !/^[A-Z]{2}$/.test(properties.iso2)
      || typeof properties.name !== "string" || !properties.name.trim()) {
      throw new Error("Country boundary identity is invalid");
    }
    if (codes.has(properties.iso2)) throw new Error(`Duplicate country boundary ${properties.iso2}`);
    const geometry = validateCountryBoundaryGeometry(feature.geometry);
    if (!geometry) throw new Error(`Country boundary geometry is invalid for ${properties.iso2}`);
    totalPositions += countPositions(geometry.coordinates);
    if (totalPositions > MAX_POSITIONS) throw new Error("Country boundary position limit exceeded");
    codes.add(properties.iso2);
    entries.push([properties.iso2, freezeGeometry(geometry)]);
  }
  return frozenIndex(entries);
}

function strictContentLength(response: Response): number | undefined {
  const raw = response.headers.get("content-length");
  if (!raw || !/^(0|[1-9][0-9]*)$/.test(raw)) return undefined;
  const value = Number(raw);
  return Number.isSafeInteger(value) ? value : undefined;
}

function isUint8Array(value: unknown): value is Uint8Array {
  return ArrayBuffer.isView(value) && Object.prototype.toString.call(value) === "[object Uint8Array]";
}

export async function fetchCountryBoundaryIndex(
  fetchImpl: typeof fetch,
  timeoutMs = LOAD_TIMEOUT_MS,
): Promise<CountryBoundaryIndex> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
  let completed = false;
  let cancelled = false;
  try {
    const response = await fetchImpl(WHO_COUNTRY_BOUNDARY_URL, { signal: controller.signal });
    if (!response.ok) throw new Error(`Country boundary request failed: ${response.status}`);
    const declaredLength = strictContentLength(response);
    if (declaredLength !== undefined && declaredLength > WHO_COUNTRY_BOUNDARY_MAX_BYTES) {
      throw new Error("Country boundary response exceeds byte limit");
    }
    if (!response.body) throw new Error("Country boundary response has no body");
    reader = response.body.getReader();
    const chunks: Uint8Array[] = [];
    let total = 0;
    while (true) {
      const result = await reader.read();
      if (result.done) break;
      if (!isUint8Array(result.value)) throw new Error("Country boundary response chunk is invalid");
      if (result.value.byteLength > WHO_COUNTRY_BOUNDARY_MAX_BYTES - total) {
        throw new Error("Country boundary response exceeds byte limit");
      }
      total += result.value.byteLength;
      chunks.push(result.value);
    }
    const bytes = new Uint8Array(total);
    let offset = 0;
    for (const chunk of chunks) {
      bytes.set(chunk, offset);
      offset += chunk.byteLength;
    }
    const text = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
    const index = parseCountryBoundaryCollection(JSON.parse(text));
    completed = true;
    return index;
  } catch (error) {
    controller.abort();
    if (reader) {
      try {
        await reader.cancel(error);
        cancelled = true;
      } catch { /* best effort */ }
    }
    throw error;
  } finally {
    clearTimeout(timer);
    if (reader) {
      if (!completed && !cancelled) {
        try { await reader.cancel(); } catch { /* best effort */ }
      }
      try { reader.releaseLock(); } catch { /* best effort */ }
    }
  }
}

export function createCountryBoundaryLoader(fetchImpl: typeof fetch = fetch) {
  let cached: Promise<CountryBoundaryIndex> | undefined;
  return () => {
    cached ??= fetchCountryBoundaryIndex(fetchImpl).catch(() => EMPTY_COUNTRY_BOUNDARIES);
    return cached;
  };
}

const loadDefaultCountryBoundaries = createCountryBoundaryLoader();

export function loadWhoCountryBoundaries() {
  return loadDefaultCountryBoundaries();
}

export function whoCountryBoundary(
  signal: NormalizedSignal,
  boundaries: CountryBoundaryIndex,
): EventGeometry | undefined {
  const geography = signal.geography;
  if (signal.source !== "WHO" || geography?.scope !== "country" || geography.confidence !== "high"
    || geography.method !== "provider-title" || typeof geography.countryCode !== "string"
    || !/^[A-Z]{2}$/.test(geography.countryCode) || geography.associatedCountries?.length !== 1
    || geography.associatedCountries[0].code !== geography.countryCode) {
    return undefined;
  }
  return boundaries[geography.countryCode];
}
